import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const PropelLogo = ({ className = 'w-8 h-8' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32" fill="none" className={className}>
    <path d="M6 26L14 6L17 15L20 6L28 26L20 21L17 26L14 21Z" fill="#4F46E5" opacity="0.95"/>
    <path d="M14 6L17 15L20 6L17 10Z" fill="#6366F1"/>
  </svg>
);

export default function PrivacyPage() {
  useDocumentTitle('Privacy Policy - Propel');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-10">
          <Link to="/" className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
          <div className="flex items-center gap-3 mb-4">
            <PropelLogo className="w-8 h-8" />
            <span className="text-xl font-heading font-bold text-gray-900">Propel</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-heading font-bold text-gray-900">Privacy Policy</h1>
          <p className="mt-2 text-sm text-gray-500">Last updated: February 27, 2026</p>
        </div>

        {/* Content */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 sm:p-10 space-y-8">

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">1. Introduction</h2>
            <p className="text-gray-600 leading-relaxed">
              Propel ("we," "our," or "us") is a SaaS platform that helps freelancers and agencies manage Upwork job proposals. This Privacy Policy explains how we collect, use, store, and protect your personal information when you use our website, application, and browser extension (collectively, the "Service").
            </p>
            <p className="text-gray-600 leading-relaxed mt-3">
              By using Propel, you agree to the collection and use of information in accordance with this policy. If you do not agree, please do not use the Service.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">2. Information We Collect</h2>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">Account Information</h3>
            <p className="text-gray-600 leading-relaxed">
              When you create an account, we collect your name, email address, and password. Passwords are hashed using bcrypt and are never stored in plain text. Your account is associated with a tenant (organization) for multi-tenant data separation.
            </p>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">Upwork Integration Data</h3>
            <p className="text-gray-600 leading-relaxed">
              If you connect your Upwork account via OAuth, we store your OAuth access and refresh tokens to sync data on your behalf. This includes job postings, proposals, chat rooms, profile information, and proposal status updates. We access only the data necessary to provide the Service.
            </p>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">Content You Create</h3>
            <p className="text-gray-600 leading-relaxed">
              We store proposals, cover letters (including AI-generated ones), portfolio items, bidding templates, saved searches, and any other content you create within Propel.
            </p>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">Usage Data</h3>
            <p className="text-gray-600 leading-relaxed">
              We collect analytics data about how you interact with the Service, including proposal success rates, response times, and feature usage. This helps us improve the product and provide you with performance insights.
            </p>

            <h3 className="text-base font-medium text-gray-800 mt-4 mb-2">Waitlist Information</h3>
            <p className="text-gray-600 leading-relaxed">
              If you join our waitlist, we collect your email address to notify you about launch updates.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">3. How We Use Your Information</h2>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
              <li><strong>Service Delivery:</strong> To provide, maintain, and improve the core features of Propel, including job syncing, proposal management, and team collaboration.</li>
              <li><strong>AI Cover Letter Generation:</strong> When you generate a cover letter, relevant context (job description, your portfolio data, past proposals) is sent to third-party AI providers to generate personalized content. See Section 4 for details.</li>
              <li><strong>Notifications:</strong> To send you real-time alerts about proposal updates, new job matches, and team activity based on your notification preferences.</li>
              <li><strong>Analytics:</strong> To provide you with performance dashboards and insights about your proposal activity.</li>
              <li><strong>Communication:</strong> To send waitlist updates, service announcements, and respond to support inquiries.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">4. Third-Party Services</h2>
            <p className="text-gray-600 leading-relaxed">
              We use the following third-party services to deliver the Service:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li><strong>OpenAI:</strong> For AI-powered cover letter generation. Job descriptions and relevant portfolio context are sent to OpenAI's API. OpenAI's data usage policies apply to data processed through their API.</li>
              <li><strong>Anthropic (Claude):</strong> As an alternative AI provider for cover letter generation and search features. Similar data is shared as with OpenAI.</li>
              <li><strong>Upwork API:</strong> To sync your job postings, proposals, messages, and profile data. We access Upwork on your behalf using your authorized OAuth credentials.</li>
              <li><strong>MongoDB Atlas:</strong> For database hosting and storage of your data.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">
              We do not sell, rent, or trade your personal information to any third party. Data shared with AI providers is used solely for generating content you request and is subject to each provider's data processing policies.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">5. Data Storage & Security</h2>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2">
              <li>All data is stored in MongoDB with tenant-level isolation. Each organization's data is logically separated by tenant identifiers.</li>
              <li>Authentication uses JSON Web Tokens (JWT) stored in httpOnly cookies, which are not accessible to client-side JavaScript.</li>
              <li>Passwords are hashed with bcrypt before storage.</li>
              <li>All API communications use HTTPS encryption in transit.</li>
              <li>Role-based access control ensures users can only access data they are authorized to view.</li>
              <li>The browser extension communicates with our servers using authenticated API calls; sensitive tokens are stored in the extension's secure storage, not in the page DOM.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">6. Data Retention</h2>
            <p className="text-gray-600 leading-relaxed">
              We retain your data for as long as your account is active or as needed to provide the Service. If you request account deletion, we will remove your personal data within 30 days, except where we are required to retain it for legal or compliance purposes. Aggregated, anonymized data may be retained indefinitely for analytics.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">7. Your Rights</h2>
            <p className="text-gray-600 leading-relaxed">
              You have the right to:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Update or correct inaccurate personal data.</li>
              <li><strong>Deletion:</strong> Request deletion of your account and associated data.</li>
              <li><strong>Data Portability:</strong> Request an export of your data in a machine-readable format.</li>
              <li><strong>Revoke Upwork Access:</strong> Disconnect your Upwork account at any time through your account settings. This stops data syncing but does not delete previously synced data.</li>
              <li><strong>Notification Preferences:</strong> Control which notifications you receive through your account settings.</li>
            </ul>
            <p className="text-gray-600 leading-relaxed mt-3">
              To exercise any of these rights, please contact us at the address listed below.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">8. Cookies & Local Storage</h2>
            <p className="text-gray-600 leading-relaxed">
              We use a minimal set of cookies and browser storage:
            </p>
            <ul className="list-disc list-inside text-gray-600 leading-relaxed space-y-2 mt-3">
              <li><strong>Authentication Cookie:</strong> An httpOnly JWT cookie that maintains your login session.</li>
              <li><strong>Local Storage:</strong> Used to persist UI preferences such as authentication state and filter settings. No tracking cookies are used.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">9. Children's Privacy</h2>
            <p className="text-gray-600 leading-relaxed">
              Propel is not intended for use by individuals under the age of 18. We do not knowingly collect personal information from children. If we learn that we have collected data from a child, we will promptly delete it.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">10. Changes to This Policy</h2>
            <p className="text-gray-600 leading-relaxed">
              We may update this Privacy Policy from time to time. If we make material changes, we will notify you by email or through a prominent notice on the Service at least 30 days before the changes take effect. Your continued use of the Service after the effective date constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-semibold text-gray-900 mb-3">11. Contact Us</h2>
            <p className="text-gray-600 leading-relaxed">
              If you have questions about this Privacy Policy or wish to exercise your data rights, please reach out to us on our <Link to="/contact" className="text-primary-600 hover:text-primary-700 underline">Contact page</Link>.
            </p>
          </section>

        </div>

        {/* Footer */}
        <div className="mt-8 flex items-center justify-between text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Propel. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <Link to="/terms" className="hover:text-gray-600 transition-colors">Terms of Service</Link>
            <Link to="/contact" className="hover:text-gray-600 transition-colors">Contact</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
